import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import api from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { 
  Users, Shield, UserCog, Eye, Crown, Search, 
  MoreVertical, Trash2, Edit, Check, X, Loader2, Inbox, KeyRound
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';

const ROLE_CONFIG = {
  admin: { label: 'Admin', color: 'bg-purple-500', icon: Crown, description: 'Full access' },
  manager: { label: 'Manager', color: 'bg-blue-500', icon: Shield, description: 'Team management' },
  pm: { label: 'Product Manager', color: 'bg-green-500', icon: UserCog, description: 'Standard access' },
  viewer: { label: 'Viewer', color: 'bg-gray-500', icon: Eye, description: 'Read-only' },
  requester: { label: 'Requester', color: 'bg-amber-500', icon: Eye, description: 'Submit use cases only' },
  intake_reviewer: { label: 'Intake reviewer', color: 'bg-teal-500', icon: Inbox, description: 'Read all intake requests only' }
};

export default function UserManagement() {
  const { user: currentUser } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [editingUser, setEditingUser] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      const res = await api.get('/auth/users');
      setUsers(res.data);
      setError(null);
    } catch (err) {
      console.error('Failed to load users:', err);
      if (err.response?.status === 403) {
        setError('You do not have permission to view users. Admin access required.');
      } else {
        setError('Failed to load users');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEditUser = (user) => {
    setEditingUser({ ...user });
    setShowEditModal(true);
  };

  const handleSaveUser = async () => {
    if (!editingUser) return;
    setSaving(true);
    try {
      await api.patch(`/auth/users/${editingUser.id}`, {
        name: editingUser.name,
        role: editingUser.role,
        is_active: editingUser.is_active
      });
      await loadUsers();
      setShowEditModal(false);
      setEditingUser(null);
    } catch (err) {
      console.error('Failed to update user:', err);
      alert('Failed to update user: ' + (err.response?.data?.detail || err.message));
    } finally {
      setSaving(false);
    }
  };

  const handleAllowMicrosoftSignIn = async (user) => {
    if (!confirm(`Allow Microsoft sign-in for ${user.email}? Only do this if you know this email belongs to the person who created the password account.`)) return;
    try {
      await api.post(`/auth/users/${user.id}/allow-microsoft-sign-in`);
      await loadUsers();
    } catch (err) {
      console.error('Failed to allow Microsoft sign-in:', err);
      alert('Failed to allow Microsoft sign-in: ' + (err.response?.data?.detail || err.message));
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!confirm('Are you sure you want to delete this user? This action cannot be undone.')) return;
    try {
      await api.delete(`/auth/users/${userId}`);
      await loadUsers();
    } catch (err) {
      console.error('Failed to delete user:', err);
      alert('Failed to delete user: ' + (err.response?.data?.detail || err.message));
    }
  };

  const filteredUsers = users.filter(u => 
    u.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
    u.role.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const isAdmin = currentUser?.role === 'admin';

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-[#98139C]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-8">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-6 text-center">
          <Shield className="w-12 h-12 text-red-500 mx-auto mb-3" />
          <h2 className="text-lg font-bold text-red-700 dark:text-red-400 mb-2">Access Denied</h2>
          <p className="text-red-600 dark:text-red-300">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-8 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#30302F] dark:text-white flex items-center gap-3">
            <Users className="text-[#98139C]" />
            User Management
          </h1>
          <p className="text-[#30302F]/80 dark:text-white/80 mt-1">
            Manage user accounts and permissions
          </p>
        </div>
        <div className="flex items-center gap-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#30302F]/60 dark:text-white/60" />
            <Input
              placeholder="Search users..."
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="pl-9 w-64"
              aria-label="Search users"
            />
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-4">
        {Object.entries(ROLE_CONFIG).map(([role, config]) => {
          const count = users.filter(u => u.role === role).length;
          const Icon = config.icon;
          return (
            <div key={role} className="bg-white dark:bg-white/5 p-4 border border-[#e3e3e3] dark:border-white/10">
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-lg ${config.color} flex items-center justify-center`}>
                  <Icon className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-[#30302F] dark:text-white">{count}</p>
                  <p className="text-sm text-[#30302F]/80 dark:text-white/80">{config.label}s</p>
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Users Table */}
      <div className="bg-white dark:bg-white/5 border border-[#e3e3e3] dark:border-white/10 overflow-hidden">
        <table className="w-full" aria-label="Users">
          <thead className="bg-[#f9f5fa] dark:bg-[#98139C]/10 border-b border-[#e3e3e3] dark:border-white/10">
            <tr>
              <th scope="col" className="text-left px-6 py-4 text-sm font-semibold text-[#30302F] dark:text-white">User</th>
              <th scope="col" className="text-left px-6 py-4 text-sm font-semibold text-[#30302F] dark:text-white">Role</th>
              <th scope="col" className="text-left px-6 py-4 text-sm font-semibold text-[#30302F] dark:text-white">Status</th>
              <th scope="col" className="text-left px-6 py-4 text-sm font-semibold text-[#30302F] dark:text-white">Joined</th>
              <th scope="col" className="text-right px-6 py-4 text-sm font-semibold text-[#30302F] dark:text-white">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredUsers.map(user => {
              const roleConfig = ROLE_CONFIG[user.role] || ROLE_CONFIG.viewer;
              const RoleIcon = roleConfig.icon;
              const isCurrentUser = user.id === currentUser?.id;
              
              return (
                <tr key={user.id} className="border-b border-[#e3e3e3] dark:border-white/5 last:border-0 hover:bg-[#f9f9f9] dark:hover:bg-white/5">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#98139C] to-[#4F0077] flex items-center justify-center text-white font-bold">
                        {user.name.charAt(0).toUpperCase()}
                      </div>
                      <div>
                        <p className="font-medium text-[#30302F] dark:text-white flex items-center gap-2">
                          {user.name}
                          {isCurrentUser && (
                            <span className="text-xs bg-[#98139C]/10 text-[#98139C] px-2 py-0.5 rounded-full">You</span>
                          )}
                        </p>
                        <p className="text-sm text-[#30302F]/70 dark:text-white/70">{user.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <Badge className={`${roleConfig.color} text-white flex items-center gap-1.5 w-fit`}>
                      <RoleIcon className="w-3 h-3" />
                      {roleConfig.label}
                    </Badge>
                  </td>
                  <td className="px-6 py-4">
                    {user.is_active !== false ? (
                      <Badge className="bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400">Active</Badge>
                    ) : (
                      <Badge className="bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400">Inactive</Badge>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-[#30302F]/80 dark:text-white/80">
                    {new Date(user.created_at).toLocaleDateString()}
                  </td>
                  <td className="px-6 py-4 text-right">
                    {isAdmin && !isCurrentUser && (
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <button className="p-2 hover:bg-[#f0f0f0] dark:hover:bg-white/10 rounded-lg transition-colors" aria-label="User actions">
                            <MoreVertical className="w-4 h-4 text-[#30302F]/80 dark:text-white/80" />
                          </button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleEditUser(user)}>
                            <Edit className="w-4 h-4 mr-2" />
                            Edit User
                          </DropdownMenuItem>
                          {user.has_password && !user.microsoft_sign_in_allowed && (
                            <DropdownMenuItem onClick={() => handleAllowMicrosoftSignIn(user)}>
                              <KeyRound className="w-4 h-4 mr-2" />
                              Allow Microsoft sign-in
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem 
                            onClick={() => handleDeleteUser(user.id)}
                            className="text-red-600 focus:text-red-600"
                          >
                            <Trash2 className="w-4 h-4 mr-2" />
                            Delete User
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                    {isCurrentUser && (
                      <span className="text-xs text-[#30302F]/60 dark:text-white/60">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        
        {filteredUsers.length === 0 && (
          <div className="p-12 text-center">
            <Users className="w-12 h-12 text-[#30302F]/30 dark:text-white/30 mx-auto mb-3" />
            <p className="text-[#30302F]/80 dark:text-white/80">No users found</p>
          </div>
        )}
      </div>

      {/* Edit User Modal */}
      <Dialog open={showEditModal} onOpenChange={setShowEditModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
          </DialogHeader>
          {editingUser && (
            <div className="space-y-4 py-4">
              <div>
                <label className="text-sm font-medium text-[#30302F] dark:text-white">Name</label>
                <Input
                  value={editingUser.name}
                  onChange={e => setEditingUser({...editingUser, name: e.target.value})}
                  className="mt-1"
                />
              </div>
              
              <div>
                <label className="text-sm font-medium text-[#30302F] dark:text-white">Email</label>
                <Input
                  value={editingUser.email}
                  disabled
                  className="mt-1 bg-gray-100 dark:bg-gray-800"
                />
              </div>
              
              <div>
                <label className="text-sm font-medium text-[#30302F] dark:text-white">Role</label>
                <select
                  value={editingUser.role}
                  onChange={e => setEditingUser({...editingUser, role: e.target.value})}
                  className="mt-1 w-full h-10 px-3 border border-[#e3e3e3] dark:border-white/10 bg-white dark:bg-white/5 text-[#30302F] dark:text-white"
                >
                  {Object.entries(ROLE_CONFIG).map(([role, config]) => (
                    <option key={role} value={role}>{config.label}</option>
                  ))}
                </select>
              </div>
              
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="is_active"
                  checked={editingUser.is_active !== false}
                  onChange={e => setEditingUser({...editingUser, is_active: e.target.checked})}
                  className="w-4 h-4"
                />
                <label htmlFor="is_active" className="text-sm text-[#30302F] dark:text-white">Account Active</label>
              </div>
              
              <div className="flex gap-3 pt-4 border-t border-[#e3e3e3] dark:border-white/10">
                <button
                  onClick={handleSaveUser}
                  disabled={saving}
                  className="flex-1 bg-[#98139C] hover:bg-[#7a0f7d] text-white rounded-full font-bold h-10 flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
                >
                  {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                  Save Changes
                </button>
                <button
                  onClick={() => setShowEditModal(false)}
                  className="px-6 border-2 border-[#e3e3e3] dark:border-white/10 text-[#30302F] dark:text-white rounded-full font-bold h-10"
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
